import PaperNote, { usePaperNotes } from './PaperNote';

function ProBadge() {
  return (
    <span
      className="ml-auto text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded"
      style={{ background: '#f5f0ff', color: '#7c3aed' }}
    >
      Pro
    </span>
  );
}

export default function Workspace({
  papers,
  onRemove,
  onClear,
  onAnalyze,
  onGenerateProtocol,
  analyzingLoading,
  protocolLoading,
  onOpenBulkImport,
  isPaidPlan = true,
  user = null,
}) {
  const FREE_TRIAL_LIMIT = 3;
  const isFreeNonEdu = user && user.plan === 'free' && !user.email?.endsWith('.edu') && !user.is_admin;
  const trialsUsed = isFreeNonEdu ? (user.trial_protocol_count || 0) : null;
  const trialsRemaining = isFreeNonEdu ? Math.max(0, FREE_TRIAL_LIMIT - trialsUsed) : null;
  const count = papers.length;
  const { notes, saveNote } = usePaperNotes();

  return (
    <div
      className="bg-white rounded-lg"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px, #fafafa 0px 0px 0px 1px inset' }}
    >
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #ebebeb' }}>
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-[#171717]">Workspace</h2>
          <span
            className="text-xs font-medium text-[#4d4d4d] px-2 py-0.5 rounded-full"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            {count}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onOpenBulkImport && (
            <button
              onClick={onOpenBulkImport}
              className="text-xs text-[#4d4d4d] hover:text-[#171717] px-2 py-0.5 rounded transition-colors"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              title="Bulk import papers"
            >
              Import
            </button>
          )}
          {count > 0 && (
            <button
              onClick={onClear}
              className="text-xs text-[#808080] hover:text-red-500 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div>
        {count === 0 ? (
          <p className="px-4 py-6 text-xs text-[#808080] text-center">
            Add papers from search results to get started.
          </p>
        ) : (
          papers.map((paper) => (
            <div
              key={paper.id}
              className="px-4 py-2"
              style={{ borderBottom: '1px solid #ebebeb' }}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-[#171717] leading-snug line-clamp-2 flex-1">
                  {notes[paper.id] && (
                    <span className="mr-1 text-xs" title="Has note">📝</span>
                  )}
                  {paper.title}
                </p>
                <button
                  onClick={() => onRemove(paper.id)}
                  className="flex-shrink-0 text-[#808080] hover:text-red-500 transition-colors text-sm mt-0.5"
                  title="Remove from workspace"
                >
                  &times;
                </button>
              </div>
              <PaperNote paperId={paper.id} notes={notes} saveNote={saveNote} />
            </div>
          ))
        )}
      </div>

      {count > 0 && (
        <div className="px-4 py-3 flex flex-col gap-2" style={{ borderTop: '1px solid #ebebeb' }}>
          {count >= 2 && (
            <button
              onClick={onAnalyze}
              disabled={analyzingLoading}
              className="w-full px-3 py-2 bg-[#171717] text-white text-xs font-medium rounded-md
                         hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {analyzingLoading ? 'Analyzing...' : 'Analyze Similarity'}
              {!isPaidPlan && <ProBadge />}
            </button>
          )}
          <button
            onClick={onGenerateProtocol}
            disabled={protocolLoading || trialsRemaining === 0}
            className="w-full px-3 py-2 text-xs font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[#171717] hover:bg-[#fafafa] flex items-center justify-center gap-2"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            {protocolLoading ? 'Generating...' : 'Generate Protocol'}
            {!isPaidPlan && trialsRemaining === null && <ProBadge />}
          </button>
          {isFreeNonEdu && (
            <p className="text-center text-[10px] text-[#808080]">
              {trialsRemaining > 0
                ? `${trialsRemaining} free trial${trialsRemaining !== 1 ? 's' : ''} remaining`
                : 'Free trials used — upgrade to Pro'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
